import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { SkeletonModule } from '@syncfusion/ej2-angular-notifications';
/**
 * Skeleton placeholder rendered while ticket data is in flight.
 *
 * The shape drawn is chosen by {@link LoaderComponent.type}. If the grid data
 * still has not arrived after 60 seconds the grid skeleton is replaced by an
 * error state so the user is not left staring at an endless placeholder.
 *
 * The failsafe is tied to each request rather than to the component: it starts
 * when `loading` turns true and is cancelled when it turns false. It used to be
 * a single timer started in `ngOnInit`, which measured time since the page was
 * opened rather than time this request had been waiting, and never reset - so a
 * minute after load the loader switched to its error state permanently and
 * every later request flashed "Error fetching data." before its results arrived.
 */
@Component({
  selector: 'app-loader',
  standalone: true,
  imports: [SkeletonModule,CommonModule],
  templateUrl: './loader.component.html',
  styleUrl: './loader.component.css'
})
export class LoaderComponent implements OnChanges, OnDestroy {
  /** CSS `conic-gradient` string for the pie-chart placeholder. */
  conicGradient: string = '';
  /** Whether the host is still loading; bound by the parent to toggle the skeleton. */
  @Input() loading: boolean = false;
  /** Shows the pie-chart error state instead of its skeleton. */
  showPieChartError: boolean = false;
  /** Shows the bar-chart error state instead of its skeleton. */
  showBarChartError: boolean = false;
  /** Whether the pie-chart skeleton is still being drawn. */
  showPieChartSkeleton: boolean = true;
  /** Whether the bar-chart skeleton is still being drawn. */
  showBarChartSkeleton: boolean = true;
  /** Whether the grid skeleton is still being drawn; cleared by the timeout in `ngOnInit`. */
  showGridSkeleton: boolean = true;
  /** Shows the grid error state after the 60-second timeout elapses. */
  showGridError: boolean = false;
  /** Which placeholder layout to render. */
  @Input() type: 'grid' | 'counts' | 'piechart' | 'barchart' = 'grid';
  /** Row widths used to give the grid skeleton a ragged, more natural look. */
  gridWidths: string[] = ['100%', '90%', '80%', '70%'];
  /** Handle for the in-flight failsafe, so a finished request can cancel it. */
  private failsafeId: ReturnType<typeof setTimeout> | null = null;

  /**
   * Restarts the failsafe whenever a request begins and cancels it when one
   * ends. Starting also clears the error state, so a loader that timed out once
   * shows a skeleton again on the next request instead of being stuck.
   * @param changes - Angular's record of the bound inputs that changed.
   * @returns Nothing.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['loading']) {
      return;
    }

    if (this.loading) {
      this.startFailsafe();
    } else {
      this.cancelFailsafe();
    }
  }

  /** Cancels any pending failsafe so it cannot fire after the view is gone. */
  ngOnDestroy(): void {
    this.cancelFailsafe();
  }

  /**
   * Puts the loader back into its skeleton state and gives this request 60
   * seconds before it is called a failure.
   * @returns Nothing.
   */
  private startFailsafe(): void {
    this.cancelFailsafe();
    this.showGridSkeleton = true;
    this.showGridError = false;

    this.failsafeId = setTimeout(() => {
      this.failsafeId = null;
      if (this.type === 'grid') {
        this.showGridSkeleton = false;
        this.showGridError = true;
      }
    }, 60000);
  }

  /**
   * Stops the pending failsafe, if there is one.
   * @returns Nothing.
   */
  private cancelFailsafe(): void {
    if (this.failsafeId !== null) {
      clearTimeout(this.failsafeId);
      this.failsafeId = null;
    }
  }
}